import React, { useState, useEffect, useCallback } from 'react';
import { useGxFeed } from '../hooks/useGxFeed';
import { useGxPolling } from '../hooks/useGxPolling';
import { useGxZones } from '../hooks/useGxZones';
import { useGxSubscription } from '../hooks/useGxSubscription';
import { GxFeedItem, GxZone, acknowledgeFeedItem, getGxConfig, getEvents, getFeed, getZones } from '../api/guestExperience';
import { LiveFeedView } from '../components/gx/LiveFeedView';
import { ZoneFilterBar } from '../components/gx/ZoneFilterBar';
import { PollingStatusBar } from '../components/gx/PollingStatusBar';
import { LiveNoteInput } from '../components/gx/LiveNoteInput';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_PAGE, BG_CARD, BG_SURFACE, FONT_HEADING } from '../components/legacy-ball/table-management/designTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EventFeedSummary {
  eventId: string;
  eventName: string;
  latestItems: GxFeedItem[];
  totalCount: number;
  zones: GxZone[];
  hasEmergency: boolean;
  lastActivity: string | null;
}

type PageView = 'grid' | 'drilldown';

// ─── Severity / Type Helpers ─────────────────────────────────────────────────

const SEVERITY_COLORS: Record<string, string> = {
  Critical: '#d32f2f',
  High: '#e65100',
  Medium: '#f9a825',
  Low: '#4caf50',
};

const TYPE_ICONS: Record<string, string> = {
  EmergencyAlert: '🚨',
  Note: '📝',
  StandardAction: '⚡',
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  title: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: '0.25rem 0 0 0',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  refreshBtn: {
    padding: '8px 16px',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  liveDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    display: 'inline-block',
    marginRight: '6px',
    animation: 'pulse 2s infinite',
  },
  liveLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#4caf50',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  // Grid: 5 columns × 2 rows
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  // Event Feed Card
  feedCard: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
    padding: '1rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '220px',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  feedCardHover: {
    backgroundColor: BG_SURFACE,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    transform: 'translateY(-2px)',
  },
  feedCardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  },
  feedCardTitle: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
    lineHeight: 1.3,
  },
  feedCardBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '10px',
    whiteSpace: 'nowrap' as const,
  },
  feedCardBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    overflow: 'hidden' as const,
  },
  feedItemRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    fontSize: '0.72rem',
    color: TEXT_MUTED,
    lineHeight: 1.4,
  },
  feedItemIcon: {
    flexShrink: 0,
    fontSize: '0.7rem',
  },
  feedItemMessage: {
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
    flex: 1,
  },
  feedItemTime: {
    fontSize: '0.65rem',
    color: TEXT_MUTED,
    opacity: 0.7,
    whiteSpace: 'nowrap' as const,
  },
  feedCardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: '0.5rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  feedCardCount: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
  },
  feedCardLastActivity: {
    fontSize: '0.65rem',
    color: TEXT_MUTED,
    fontStyle: 'italic' as const,
  },
  emergencyIndicator: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: '#d32f2f',
  },
  // Zone chips in card
  zoneChips: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap' as const,
    marginBottom: '6px',
  },
  zoneChip: {
    fontSize: '0.6rem',
    padding: '1px 5px',
    borderRadius: '8px',
    fontWeight: 600,
    color: '#fff',
  },
  // Drilldown view
  drilldownHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
  },
  backBtn: {
    padding: '8px 14px',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  drilldownTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.4rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  drilldownSubtitle: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: '0.25rem 0 0 0',
  },
  drilldownContent: {
    maxWidth: '100%',
  },
  spinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4rem',
    fontSize: '1rem',
    color: TEXT_MUTED,
  },
  emptyCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic' as const,
  },
  noteInputWrapper: {
    position: 'sticky' as const,
    bottom: 0,
    backgroundColor: BG_PAGE,
    padding: '0.75rem 0',
    borderTop: `1px solid ${BORDER_LIGHT}`,
    zIndex: 10,
  },
};

// ─── Event Feed Card Component ───────────────────────────────────────────────

function EventFeedCard({
  summary,
  onClick,
}: {
  summary: EventFeedSummary;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  const cardStyle: React.CSSProperties = {
    ...styles.feedCard,
    ...(hovered ? styles.feedCardHover : {}),
  };

  const countBadgeStyle: React.CSSProperties = {
    ...styles.feedCardBadge,
    backgroundColor: summary.hasEmergency ? '#ffebee' : '#e8f5e9',
    color: summary.hasEmergency ? '#d32f2f' : PRIMARY,
  };

  return (
    <div
      style={cardStyle}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`View activity feed for ${summary.eventName}`}
    >
      {summary.hasEmergency && <div style={styles.emergencyIndicator} />}

      {/* Header */}
      <div style={styles.feedCardHeader}>
        <h3 style={styles.feedCardTitle}>{summary.eventName}</h3>
        <span style={countBadgeStyle}>{summary.totalCount}</span>
      </div>

      {/* Zone chips */}
      {summary.zones.length > 0 && (
        <div style={styles.zoneChips}>
          {summary.zones.slice(0, 3).map((z) => (
            <span key={z.id} style={{ ...styles.zoneChip, backgroundColor: z.color }}>
              {z.name}
            </span>
          ))}
          {summary.zones.length > 3 && (
            <span style={{ ...styles.zoneChip, backgroundColor: TEXT_MUTED }}>
              +{summary.zones.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Feed items preview */}
      <div style={styles.feedCardBody}>
        {summary.latestItems.length === 0 ? (
          <div style={styles.emptyCard}>No activity yet</div>
        ) : (
          summary.latestItems.slice(0, 4).map((item) => (
            <div key={item.id} style={styles.feedItemRow}>
              <span style={styles.feedItemIcon}>{TYPE_ICONS[item.type] || '📌'}</span>
              <span style={{
                ...styles.feedItemMessage,
                color: item.severity === 'Critical' || item.severity === 'High'
                  ? SEVERITY_COLORS[item.severity]
                  : TEXT_MUTED,
                fontWeight: item.type === 'EmergencyAlert' ? 600 : 400,
              }}>
                {item.message}
              </span>
              <span style={styles.feedItemTime}>{timeAgo(item.createdAt)}</span>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div style={styles.feedCardFooter}>
        <span style={styles.feedCardCount}>
          {summary.totalCount} item{summary.totalCount !== 1 ? 's' : ''}
        </span>
        <span style={styles.feedCardLastActivity}>
          {summary.lastActivity ? timeAgo(summary.lastActivity) : 'No activity'}
        </span>
      </div>
    </div>
  );
}

// ─── Drilldown View Component ────────────────────────────────────────────────

function DrilldownView({
  eventId,
  eventName,
  onBack,
}: {
  eventId: string;
  eventName: string;
  onBack: () => void;
}) {
  const [polledItems, setPolledItems] = useState<GxFeedItem[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]);

  const { subscription, isLoading: subLoading, subscribe } = useGxSubscription(eventId);
  const { zones } = useGxZones(eventId);
  const zoneFilter = selectedZones.length > 0 ? selectedZones : undefined;
  const { items: feedItems, isLoading: feedLoading, loadMore, hasMore } = useGxFeed(eventId, zoneFilter);

  const isSubscribed = subscription !== null;

  // Auto-subscribe
  useEffect(() => {
    if (!subLoading && !isSubscribed && eventId) {
      subscribe().catch(() => {});
    }
  }, [subLoading, isSubscribed, subscribe, eventId]);

  const handleNewItems = useCallback((newItems: GxFeedItem[]) => {
    setPolledItems((prev) => [...newItems, ...prev]);
  }, []);

  const { isPolling, lastPollTime, error: pollingError } = useGxPolling(eventId, isSubscribed, handleNewItems);

  const allItems = [...polledItems, ...feedItems];
  const activeZones = zones.filter((z) => z.active);

  const handleToggleZone = useCallback((zoneId: string) => {
    setSelectedZones((prev) =>
      prev.includes(zoneId) ? prev.filter((id) => id !== zoneId) : [...prev, zoneId],
    );
  }, []);

  const handleNoteCreated = useCallback((item: GxFeedItem) => {
    setPolledItems((prev) => [item, ...prev]);
  }, []);

  const handleAcknowledge = useCallback(
    async (itemId: string) => {
      try {
        const updated = await acknowledgeFeedItem(eventId, itemId);
        setPolledItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, acknowledged: updated.acknowledged, acknowledgedAt: updated.acknowledgedAt, acknowledgedBy: updated.acknowledgedBy }
              : item,
          ),
        );
      } catch {
        // silent
      }
    },
    [eventId],
  );

  return (
    <div style={styles.drilldownContent}>
      {/* Back + Title */}
      <div style={styles.drilldownHeader}>
        <button style={styles.backBtn} onClick={onBack} aria-label="Back to feed grid">
          ← Back
        </button>
        <div>
          <h2 style={styles.drilldownTitle}>📡 {eventName}</h2>
          <p style={styles.drilldownSubtitle}>Real-time activity feed — full view</p>
        </div>
      </div>

      {/* Polling Status */}
      <PollingStatusBar
        isPolling={isPolling}
        lastPollTime={lastPollTime}
        error={pollingError}
      />

      {/* Zone Filter */}
      {activeZones.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <ZoneFilterBar
            zones={activeZones}
            selectedZones={selectedZones}
            onToggleZone={handleToggleZone}
          />
        </div>
      )}

      {/* Feed */}
      {feedLoading ? (
        <div style={styles.spinner}>Loading feed...</div>
      ) : (
        <LiveFeedView
          items={allItems as any}
          onAcknowledge={handleAcknowledge}
          onLoadMore={loadMore}
          hasMore={hasMore}
        />
      )}

      {/* Note Input */}
      <div style={styles.noteInputWrapper}>
        <LiveNoteInput eventId={eventId} zones={activeZones} onNoteCreated={handleNoteCreated} />
      </div>
    </div>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────

export function KongosaActivityFeedPage() {
  const [summaries, setSummaries] = useState<EventFeedSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<PageView>('grid');
  const [selectedEvent, setSelectedEvent] = useState<{ id: string; name: string } | null>(null);
  const [feedName, setFeedName] = useState('Kongosa Activity Feed');
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  // Fetch all events and their feed summaries
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsData, config] = await Promise.all([
        getEvents(),
        getGxConfig().catch(() => ({ feedName: 'Kongosa Activity Feed', muted: false })),
      ]);
      setFeedName(config.feedName ? `${config.feedName} — Desktop Monitor` : 'Kongosa Activity Feed');

      // Fetch feed summary for each event (latest 5 items each)
      const summaryPromises = eventsData.map(async (evt) => {
        try {
          const [feedResponse, zones] = await Promise.all([
            getFeed(evt.id, { limit: 5 }),
            getZones(evt.id).catch(() => [] as GxZone[]),
          ]);
          const hasEmergency = feedResponse.items.some((item) => item.type === 'EmergencyAlert' && !item.acknowledged);
          const lastActivity = feedResponse.items.length > 0 ? feedResponse.items[0].createdAt : null;
          return {
            eventId: evt.id,
            eventName: evt.name,
            latestItems: feedResponse.items,
            totalCount: feedResponse.count,
            zones: (zones as GxZone[]).filter((z) => z.active),
            hasEmergency,
            lastActivity,
          } as EventFeedSummary;
        } catch {
          return {
            eventId: evt.id,
            eventName: evt.name,
            latestItems: [],
            totalCount: 0,
            zones: [],
            hasEmergency: false,
            lastActivity: null,
          } as EventFeedSummary;
        }
      });

      const results = await Promise.all(summaryPromises);
      setSummaries(results);
      setLastRefreshed(new Date());
    } catch {
      setSummaries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30s in grid view
  useEffect(() => {
    if (view !== 'grid') return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [view, fetchData]);

  const handleCardClick = (eventId: string, eventName: string) => {
    setSelectedEvent({ id: eventId, name: eventName });
    setView('drilldown');
  };

  const handleBack = () => {
    setView('grid');
    setSelectedEvent(null);
    fetchData(); // Refresh summaries when returning to grid
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.spinner}>Loading activity feeds...</div>
      </div>
    );
  }

  // Drilldown mode — full-page real-time feed
  if (view === 'drilldown' && selectedEvent) {
    return (
      <div style={styles.page}>
        <DrilldownView
          eventId={selectedEvent.id}
          eventName={selectedEvent.name}
          onBack={handleBack}
        />
      </div>
    );
  }

  // Grid mode — 2×5 event feed cards
  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.headerLeft}>
            <h1 style={styles.title}>📡 {feedName}</h1>
            <span style={styles.liveLabel}>
              <span style={styles.liveDot} />
              LIVE
            </span>
          </div>
          <p style={styles.subtitle}>
            All event activity feeds at a glance — click any card to drill into its real-time feed
          </p>
        </div>
        <div style={styles.headerRight}>
          <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>
            Last refreshed: {lastRefreshed.toLocaleTimeString()}
          </span>
          <button style={styles.refreshBtn} onClick={fetchData} aria-label="Refresh all feeds">
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Event Feed Cards Grid: 2 rows × 5 columns */}
      {summaries.length === 0 ? (
        <div style={styles.spinner}>No events available.</div>
      ) : (
        <div style={styles.cardGrid}>
          {summaries.map((summary) => (
            <EventFeedCard
              key={summary.eventId}
              summary={summary}
              onClick={() => handleCardClick(summary.eventId, summary.eventName)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
